import React from 'react';
import PropTypes from 'prop-types';
import { push } from 'react-router-redux';
import { connect } from 'react-redux';
import RedBox from 'redbox-react';
import styled from 'styled-components';
import { Spin } from 'antd';
import Sidebar from './Sidebar';
import {
  tmRoutes,
  nsRoutes,
  crsRoutes,
  trmRoutes,
  CMRoutes,
  FramlRoutes,
  sandboxRoutes
} from '../../routes';
import Topbar from './Topbar';
import Footbar from './Footbar';
import NotificationListener from './NotificationListener';
import {
  fetchAppContext,
  fetchUsersModulesRequest,
  productLoginRequest,
  setCurrentProductLogin,
  postLogoutRequest
} from '../../redux/actions/authentication';
import style from './index.scss';
import { getCurrentAppByPath, getAppName } from './appUtils';
// switchApp does no permission check; module routes handle access control.
// A module switch navigates once its product login completes (componentDidUpdate).
// import loadable from '@loadable/component';

export const LoadingWrapper = styled.div`
  position: absolute;
  z-index: 10000;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.3);
`;

// const LoadableNopp = loadable(() => import('nopp'));

class App extends React.Component {
  // eslint-disable-line react/prefer-stateless-function
  constructor(props) {
    super(props);

    this.state = {
      menuCollaps: true,
      selectedItem: 1,
      emerging: true,
      appError: null
    };

    this.toggleMenu = this.toggleMenu.bind(this);
    this.changeActiveItem = this.changeActiveItem.bind(this);
    this.onPostLogout = this.onPostLogout.bind(this);
    this.onDismiss = this.onDismiss.bind(this);
    this.switchApp = this.switchApp.bind(this);
  }

  static getDerivedStateFromProps(nextProps) {
    const { app } = nextProps;

    if (app && app.tmMetadataResp) {
      const tmMetadata = app.tmMetadataResp.data;
      localStorage.setItem('tm_metadata', JSON.stringify(tmMetadata));
    }
    return null;
  }

  toggleMenu() {
    this.setState({
      menuCollaps: !this.state.menuCollaps
    });
  }

  changeActiveItem(id) {
    this.setState({
      ...this.state,
      selectedItem: id
    });
  }

  onPostLogout() {
    this.props.postLogout({
      token: localStorage.getItem('id_token')
    });
  }

  onDismiss() {
    this.setState({
      ...this.state,
      emerging: false
    });
  }

  switchApp(selectedModule) {
    const moduleRoutes = {
      tm: '/app/tm',
      'smart-screening': '/app/smart-screening',
      crs: '/app/crs',
      sandbox: '/app/sandbox',
      cm: '/app/cm',
      framl: '/app/framl'
    };
    const route = moduleRoutes[selectedModule];
    if (route) {
      this.props.navigateTo(route);
    }
  }

  onPageError = () => {
    window.location.reload();
  };

  onShowMore = () => {
    if (this.state.timeOut) {
      clearTimeout(this.state.timeOut);
    }
  };

  componentDidMount() {
    const {
      dispatch,
      authentication: { data, userModules, currentProductLogin },
      location
    } = this.props;

    if (!data) {
      dispatch(fetchAppContext());
    }

    if (!userModules?.data || userModules.data.length === 0) {
      dispatch(fetchUsersModulesRequest());
    }

    if (currentProductLogin && location.pathname === '/app') {
      const moduleName = getAppName(currentProductLogin)?.toLowerCase();
      if (moduleName && moduleName !== '' && moduleName !== '404') {
        this.switchApp(moduleName);
      }
    }

    // LoadableNopp.load();
  }

  componentDidUpdate(prevProps) {
    const {
      dispatch,
      authentication: { currentProductLogin, userModules, productLogin },
      location
    } = this.props;

    if (currentProductLogin !== prevProps.authentication.currentProductLogin) {
      const moduleName = getAppName(currentProductLogin)?.toLowerCase();

      if (moduleName !== '' && moduleName !== '404') {
        // The dropdown dispatches productLoginRequest + setCurrentProductLogin together,
        // so authentication.data still holds the previous module's policies here.
        // Navigate once the new module's policies are loaded (see below).
        if (productLogin?.isInProgress) {
          this.pendingModuleSwitch = moduleName;
        } else {
          this.switchApp(moduleName);
        }

        this.setState({
          ...this.state,
          selectedItem: getActiveSidebarByPath(location.pathname),
          timeOut: setTimeout(this.onDismiss, 10000)
        });
      }
    }

    const wasLoading = prevProps.authentication.productLogin?.isInProgress;
    const doneLoading = !productLogin?.isInProgress;
    if (wasLoading && doneLoading && this.pendingModuleSwitch) {
      const pendingModule = this.pendingModuleSwitch;
      this.pendingModuleSwitch = null;
      if (!productLogin?.error) {
        this.switchApp(pendingModule);
      }
    } else if (
      wasLoading &&
      doneLoading &&
      currentProductLogin &&
      location.pathname === '/app'
    ) {
      const moduleName = getAppName(currentProductLogin)?.toLowerCase();
      if (moduleName && moduleName !== '' && moduleName !== '404') {
        this.switchApp(moduleName);
      }
    }

    const prevModules = prevProps.authentication.userModules?.data;
    const currModules = userModules?.data;
    if (
      !currentProductLogin &&
      currModules?.length > 0 &&
      (!prevModules || prevModules.length === 0)
    ) {
      const defaultModule =
        currModules.find((m) => m.isDefault) || currModules[0];
      dispatch(setCurrentProductLogin(defaultModule));
      dispatch(productLoginRequest(defaultModule));
    }
  }

  componentDidCatch(error, info) {
    this.setState({
      ...this.state,
      appError: error
    });
  }

  componentWillUnmount() {
    clearTimeout(this.state.timeOut);
  }

  render() {
    const { app, location, authentication } = this.props;
    const { appError } = this.state;
    const {
      authentication: { currentProductLogin }
    } = this.props;
    const moduleName = getAppName(currentProductLogin)?.toLowerCase();

    const isModulesNotReady = !authentication.userModules?.data;
    const isAppLoader =
      authentication.productLogin?.isInProgress ||
      authentication.userModules?.isInProgress ||
      authentication.changeRole?.isInProgress ||
      isModulesNotReady;

    if (appError && process.env.mode == 'dev') {
      return <RedBox error={appError} />;
    }

    return (
      <div className={style.wrapper}>
        {isAppLoader ? (
          <LoadingWrapper>
            <Spin size="large" />
          </LoadingWrapper>
        ) : null}
        <Sidebar
          location={location}
          appName={moduleName}
          selectedItem={this.state.selectedItem}
          menuCollaps={this.state.menuCollaps}
          changeActiveItem={this.changeActiveItem}
          toggleMenu={this.toggleMenu}
          navigateTo={this.props.navigateTo}
          authentication={authentication}
        />
        <Topbar
          authentication={authentication}
          location={location}
          appName={moduleName}
          menuCollaps={this.state.menuCollaps}
          postLogout={this.onPostLogout}
          onSwitchApp={this.switchApp}
          isLoading={app.isFetching}
        />
        <div
          className={
            this.state.menuCollaps
              ? style.mainContentCollaps
              : style.mainContent
          }>
          {/* {false && appName == 'tm' ? (
            <DismissBar
              type="ALERT"
              message=" For segment 'SG-PFS', the number of alerts produced in the past month is 50% above the monthly average."
              cb={this.onDismiss}
              onShowMore={this.onShowMore}
            />
          ) : (
            ''
          )} */}
          {moduleName == 'smart-screening' && nsRoutes(authentication)}
          {moduleName == 'tm' && tmRoutes(authentication)}
          {moduleName == 'crs' && crsRoutes(authentication)}
          {(moduleName == 'trm' || moduleName == 'tm') &&
            trmRoutes(authentication)}
          {/* TODO: Check if sandbox module name check is required */}
          {moduleName == 'sandbox' && sandboxRoutes(authentication)}
          {moduleName == 'cm' && <CMRoutes authentication={authentication} />}
          {moduleName == 'framl' && (
            <FramlRoutes authentication={authentication} />
          )}
        </div>
        <Footbar />
        <NotificationListener />
      </div>
    );
  }
}

App.propTypes = {
  dispatch: PropTypes.func.isRequired,
  app: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  authentication: PropTypes.object.isRequired,
  navigateTo: PropTypes.func,
  postLogout: PropTypes.func
};

const getActiveSidebarByPath = (path) => {
  if (path.indexOf('investigation') >= 0) return 2;

  return 1;
};

function mapStateToProps(state, ownProps = {}) {
  return {
    app: state.app,
    authentication: state.authentication,
    location: state.router.location
  };
}

function mapDispatchToProps(dispatch, ownProps = {}) {
  return {
    dispatch,
    navigateTo: (location) => {
      dispatch(push(location));
    },
    postLogout: (payload) => {
      dispatch(postLogoutRequest(payload));
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
